// Generated by GitHub Copilot
import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Badge,
  Card,
  OverlayTrigger,
  Tooltip,
  ProgressBar,
} from "react-bootstrap";
import {
  FaHistory,
  FaEye,
  FaCalendarAlt,
  FaInfoCircle,
  FaRocket,
  FaChartLine,
  FaLayerGroup,
  FaExchangeAlt,
  FaClock,
  FaList,
  FaFilter,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { VersionHistoryAPI } from "../../connections/VersionHistory";
import ChangesGrid from "./ChangesGrid";
import dayjs from "dayjs";
import "./VersionHistory.css";

const relativeTime = require("dayjs/plugin/relativeTime");
const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const VersionHistory = forwardRef(
  ({ problemId, onVersionRestore, onVersionCountChange, onBeforeNavigate }, ref) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [performanceMetrics, setPerformanceMetrics] = useState({
      loadTime: 0,
      changeCount: 0,
      largeDatasetWarning: false,
    });
    // Generated by GitHub Copilot
    // State for controlling the Show All toggle
    const [showAllVersions, setShowAllVersions] = useState(false);
    const navigate = useNavigate(); // Generated by GitHub Copilot

    // Generated by GitHub Copilot
    // Filter versions to only show those with audit trail entries (changes) by default
    // When showAllVersions is true, show all versions regardless of changes
    const filteredVersions = useMemo(() => {
      if (!versions || versions.length === 0) {
        return [];
      }

      // If showAllVersions is enabled, return all versions
      if (showAllVersions) {
        return versions;
      }

      // Filter to only show versions that have changes (audit trail entries)
      return versions.filter((version) => {
        // Always show the latest version regardless of changes
        if (version.isLatest) {
          return true;
        }

        // Show versions that have actual changes
        return version.changes && version.changes.length > 0;
      });
    }, [versions, showAllVersions]);

    // Enhanced loading with performance tracking
    const loadVersionHistory = useCallback(async () => {
      const startTime = performance.now();
      setLoading(true);
      setError(null);

      try {
        const response = await VersionHistoryAPI.listVersions(problemId);
        if (response.success) {
          const versionList = response.versions || [];
          setVersions(versionList);

          // Update performance metrics
          const endTime = performance.now();
          const loadTime = Math.round(endTime - startTime);
          const changeCount = versionList.reduce(
            (sum, v) => sum + (v.changes?.length || 0),
            0
          );

          setPerformanceMetrics({
            loadTime,
            changeCount,
            largeDatasetWarning: changeCount > 100,
          });

          // Notify parent component about version count change
          if (onVersionCountChange) {
            onVersionCountChange(versionList.length);
          }
        } else {
          setError(response.error || "Failed to load version history");
        }
      } catch (err) {
        setError("Failed to load version history: " + err.message);
      } finally {
        setLoading(false);
      }
    }, [problemId, onVersionCountChange]);

    // Generated by GitHub Copilot
    // Expose refresh method to parent components via ref
    useImperativeHandle(
      ref,
      () => ({
        refreshVersions: loadVersionHistory,
      }),
      [loadVersionHistory]
    );

    useEffect(() => {
      if (problemId) {
        loadVersionHistory();
      }
    }, [problemId, loadVersionHistory]);

    // Generated by GitHub Copilot
    // Update parent component when filtered versions change
    useEffect(() => {
      if (onVersionCountChange && filteredVersions) {
        onVersionCountChange(filteredVersions.length);
      }
    }, [filteredVersions, onVersionCountChange, showAllVersions]);

    // Generated by GitHub Copilot
    const handleViewVersion = (versionId) => {
      // Navigate to the Viewer component with the version parameter
      const destination = `/problem/view/${problemId}/version/${versionId}`;
      // Ignores a rewritten path: a version id belongs to this record's own
      // history, so after a promotion it does not exist under the new number.
      const proceed = () => navigate(destination);
      // US2138216: the host page may hold this exit (an unsaved PRBT number).
      if (onBeforeNavigate) {
        onBeforeNavigate(destination, proceed);
      } else {
        proceed();
      }
    };

    // Performance optimization: memoized version statistics
    const versionStats = useMemo(() => {
      if (!filteredVersions || filteredVersions.length === 0) {
        return {
          totalChanges: 0,
          mostActiveVersion: null,
          avgChangesPerVersion: 0,
        };
      }

      const totalChanges = filteredVersions.reduce(
        (sum, version) => sum + (version.changes ? version.changes.length : 0),
        0
      );

      const mostActiveVersion = filteredVersions.reduce((max, version) => {
        const changeCount = version.changes ? version.changes.length : 0;
        const maxChangeCount = max.changes ? max.changes.length : 0;
        return changeCount > maxChangeCount ? version : max;
      }, filteredVersions[0]);

      const avgChangesPerVersion =
        filteredVersions.length > 0
          ? Math.round(totalChanges / filteredVersions.length)
          : 0;

      return {
        totalChanges,
        mostActiveVersion,
        avgChangesPerVersion,
        hasLargeDataset: totalChanges > 100, // Flag for large datasets
      };
    }, [filteredVersions]);

    // Enhanced loading state with progress indication
    if (loading) {
      return (
        <Container>
          <Row>
            <Col className="text-center">
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-4">
                  <div className="mb-3">
                    <Spinner animation="border" role="status" variant="primary">
                      <span className="visually-hidden">
                        Loading version history...
                      </span>
                    </Spinner>
                  </div>
                  <h5 className="text-muted mb-2">Loading Version History</h5>
                  <p className="text-muted small mb-3">
                    Retrieving field-level changes and revision data...
                  </p>
                  <ProgressBar
                    animated
                    now={100}
                    variant="info"
                    style={{ height: "4px" }}
                  />

                  {performanceMetrics.loadTime > 2000 && (
                    <div className="mt-3">
                      <Alert variant="info" className="mb-0 py-2">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          <FaInfoCircle className="me-2" />
                        </span>
                        <small>
                          Large dataset detected - this may take a moment
                        </small>
                      </Alert>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      );
    }

    if (error) {
      return (
        <Container>
          <Row>
            <Col>
              <Alert variant="danger">
                <Alert.Heading>Error Loading Version History</Alert.Heading>
                <p>{error}</p>
                <Button variant="outline-danger" onClick={loadVersionHistory}>
                  Try Again
                </Button>
              </Alert>
            </Col>
          </Row>
        </Container>
      );
    }

    if (!versions || versions.length === 0) {
      return (
        <Container>
          <Row>
            <Col>
              <Alert variant="info">
                <FaHistory className="me-2" />
                No version history available for this problem record.
              </Alert>
            </Col>
          </Row>
        </Container>
      );
    }

    return (
      <Container>
        {/* Enhanced Statistics and Performance Indicators */}
        <Row className="mb-3">
          <Col>
            <Card className="border-0 bg-light">
              <Card.Body className="py-2">
                <Row className="align-items-center">
                  <Col md={8}>
                    <div className="d-flex align-items-center">
                      <FaChartLine className="text-primary me-2" />
                      <strong className="me-3">Version Statistics:</strong>

                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {versions.length} Version
                            {versions.length !== 1 ? "s" : ""} Available
                          </Tooltip>
                        }
                      >
                        <div
                          className="d-flex align-items-center me-3 text-primary"
                          style={{ cursor: "help" }}
                        >
                          <FaLayerGroup size={16} className="me-1" />
                          <span className="small fw-bold">
                            {versions.length}
                          </span>
                        </div>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {versionStats.totalChanges} Total Field Change
                            {versionStats.totalChanges !== 1 ? "s" : ""} Across
                            All Versions
                          </Tooltip>
                        }
                      >
                        <div
                          className="d-flex align-items-center me-3 text-info"
                          style={{ cursor: "help" }}
                        >
                          <FaExchangeAlt size={16} className="me-1" />
                          <span className="small fw-bold">
                            {versionStats.totalChanges}
                          </span>
                        </div>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Average of {versionStats.avgChangesPerVersion}{" "}
                            Change
                            {versionStats.avgChangesPerVersion !== 1
                              ? "s"
                              : ""}{" "}
                            Per Version
                          </Tooltip>
                        }
                      >
                        <div
                          className="d-flex align-items-center text-secondary"
                          style={{ cursor: "help" }}
                        >
                          <FaClock size={16} className="me-1" />
                          <span className="small fw-bold">
                            {versionStats.avgChangesPerVersion}
                          </span>
                        </div>
                      </OverlayTrigger>
                    </div>
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex align-items-center justify-content-end">
                      {performanceMetrics.loadTime > 0 && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              Loaded {performanceMetrics.changeCount} changes in{" "}
                              {performanceMetrics.loadTime}ms
                            </Tooltip>
                          }
                        >
                          <small className="text-muted me-3">
                            <FaRocket className="me-1" />
                            {performanceMetrics.loadTime}ms
                          </small>
                        </OverlayTrigger>
                      )}
                      {/* Generated by GitHub Copilot */}
                      {/* Show All toggle button */}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {showAllVersions
                              ? "Hide versions with no changes"
                              : "Show all versions including those with no changes"}
                          </Tooltip>
                        }
                      >
                        <Button
                          variant={
                            showAllVersions ? "primary" : "outline-primary"
                          }
                          size="sm"
                          className="me-2"
                          onClick={() => setShowAllVersions(!showAllVersions)}
                          aria-label={
                            showAllVersions
                              ? "Currently showing all versions. Click to hide versions with no changes"
                              : "Currently hiding versions with no changes. Click to show all versions"
                          }
                          aria-checked={showAllVersions}
                          role="switch"
                        >
                          {showAllVersions ? (
                            <>
                              <FaFilter className="me-1" aria-hidden="true" />
                              Filtered
                            </>
                          ) : (
                            <>
                              <FaList className="me-1" aria-hidden="true" />
                              Show All
                            </>
                          )}
                        </Button>
                      </OverlayTrigger>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={loadVersionHistory}
                      >
                        Refresh
                      </Button>
                    </div>
                  </Col>
                </Row>

                {performanceMetrics.largeDatasetWarning && (
                  <Row className="mt-2">
                    <Col>
                      <Alert variant="warning" className="mb-0 py-2">
                        <FaInfoCircle className="me-2" />
                        <small>
                          Large dataset ({performanceMetrics.changeCount}{" "}
                          changes) - consider using filters for better
                          performance
                        </small>
                      </Alert>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Generated by GitHub Copilot */}
        {/* Filtering Status Indicator */}
        {!showAllVersions &&
          versions &&
          filteredVersions.length < versions.length && (
            <Row className="mb-3">
              <Col>
                <Alert
                  variant="info"
                  className="border-0 d-flex align-items-center py-2 px-3"
                >
                  <FaFilter className="me-2" aria-hidden="true" />
                  <span className="mb-0">
                    <strong>Filtered View:</strong> Showing{" "}
                    {filteredVersions.length} of {versions.length} versions (
                    {versions.length - filteredVersions.length} versions with no
                    changes are hidden)
                  </span>
                </Alert>
              </Col>
            </Row>
          )}

        {/* Version History Cards */}
        {filteredVersions.map((version, index) => (
          <Row key={version.versionId} className="mb-3">
            <Col>
              <Card
                className={`version-card ${
                  version.isLatest ? "latest-version" : ""
                } ${
                  version.changes && version.changes.length > 0
                    ? "has-changes"
                    : ""
                }`}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="version-info">
                      <h6 className="mb-2">
                        {version.isLatest && (
                          <Badge bg="success" className="me-2">
                            Current
                          </Badge>
                        )}
                        Version {index + 1}
                        {version.changes && version.changes.length > 0 && (
                          <Badge bg="info" className="ms-2" pill>
                            {version.changes.length} change
                            {version.changes.length !== 1 ? "s" : ""}
                          </Badge>
                        )}
                      </h6>

                      <div className="version-details">
                        <div className="detail-row">
                          <FaCalendarAlt className="me-1" />
                          <span className="detail-label">Modified:</span>
                          <span className="detail-value">
                            {version.lastModified}
                          </span>
                          <span className="text-muted ms-2">
                            (
                            {dayjs(
                              version.lastModified,
                              "MM/DD/YYYY HH:mm z"
                            ).fromNow()}
                            )
                          </span>
                        </div>

                        {version.location && (
                          <div className="detail-row">
                            <span className="detail-label">Status:</span>
                            <span className="detail-value">
                              {version.location === "active"
                                ? "Active"
                                : version.location === "resolved"
                                ? "Resolved"
                                : version.location === "archived"
                                ? "Archived"
                                : version.location}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="version-actions">
                      {/* Generated by GitHub Copilot */}
                      {/* Only show View button for non-current versions */}
                      {!version.isLatest && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => handleViewVersion(version.versionId)}
                        >
                          <FaEye className="me-1" />
                          View
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Generated by GitHub Copilot */}
                  {/* Field-level changes grid */}
                  <ChangesGrid
                    changes={version.changes}
                    version={version}
                    enableAdvancedFeatures={true}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ))}
      </Container>
    );
  }
);

export default VersionHistory;
